/**
 * Environment Configuration
 * Centralizes all environment variables and provides validation
 */

interface EnvironmentConfig {
  // API Configuration
  apiUrl: string;

  // Stripe Configuration
  stripePublishableKey: string;

  // Application Configuration
  appEnvironment: 'development' | 'staging' | 'production';
  appVersion: string;

  // Feature Flags
  enableAnalytics: boolean;
  enableErrorTracking: boolean;
}

/**
 * Validates that all required environment variables are present
 */
function validateEnvironment(): EnvironmentConfig {
  const requiredVars = {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    REACT_APP_STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development',
  };

  // Check for missing required variables
  const missingVars = Object.entries(requiredVars)
    .filter(([key, value]) => !value && key !== 'REACT_APP_ENVIRONMENT')
    .map(([key]) => key);

  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}\n` +
      'Please ensure all required environment variables are set in your .env file.'
    );
  }

  // Validate Stripe key format
  const stripeKey = requiredVars.REACT_APP_STRIPE_PUBLISHABLE_KEY!;
  if (!stripeKey.startsWith('pk_')) {
    throw new Error('REACT_APP_STRIPE_PUBLISHABLE_KEY must be a valid Stripe publishable key (starts with pk_)');
  }

  // Warn about test keys in production
  if (requiredVars.REACT_APP_ENVIRONMENT === 'production' && stripeKey.includes('test')) {
    console.warn('WARNING: Using test Stripe key in production environment');
  }

  return {
    apiUrl: requiredVars.REACT_APP_API_URL!,
    stripePublishableKey: stripeKey,
    appEnvironment: requiredVars.REACT_APP_ENVIRONMENT as 'development' | 'staging' | 'production',
    appVersion: process.env.REACT_APP_VERSION || '1.0.0',
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    enableErrorTracking: process.env.REACT_APP_ENABLE_ERROR_TRACKING === 'true',
  };
}

// Export validated configuration
export const config = validateEnvironment();

// Export individual values for convenience
export const {
  apiUrl,
  stripePublishableKey,
  appEnvironment,
  appVersion,
  enableAnalytics,
  enableErrorTracking,
} = config;

// Helper functions
export const isDevelopment = appEnvironment === 'development';
export const isProduction = appEnvironment === 'production';
export const isStaging = appEnvironment === 'staging';